import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Bookmark, BookmarkCheck } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScoreBadge({ score }) {
  if (score === null || score === undefined) return null;
  const color = score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#94a3b8";
  return (
    <span style={{ fontSize: "11px", fontWeight: 700, color, background: `${color}1A`, borderRadius: "999px", padding: "2px 8px", flexShrink: 0 }}>
      {score}%
    </span>
  );
}

// Compact result cards rendered inside InfoCity IA's chat (Fase 3 — IA Action
// Mode). Real data only — job_id/application_id always point to a real record
// fetched server-side, never invented.
export function AIResultCard({ result, t }) {
  const [saved, setSaved] = useState(!!result.is_saved);
  const [saving, setSaving] = useState(false);

  if (result.type === "job") {
    const toggleSave = async () => {
      setSaving(true);
      try {
        if (saved) await axios.delete(`${API}/saved-jobs/${result.job_id}`, { headers: getAuthHeaders() });
        else await axios.post(`${API}/saved-jobs/${result.job_id}`, {}, { headers: getAuthHeaders() });
        setSaved((v) => !v);
      } catch { /* silently ignore — non-critical UI action */ }
      finally { setSaving(false); }
    };
    return (
      <div data-testid={`ai-result-job-${result.job_id}`} style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "12px", padding: "10px 12px", marginTop: "6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", lineHeight: 1.3 }}>{result.title}</p>
            <p style={{ fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif" }}>{result.company_name}</p>
          </div>
          <ScoreBadge score={result.match_score} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px", fontSize: "11px", color: "#94A3B8" }}>
          {result.location && <span style={{ display: "flex", alignItems: "center", gap: "3px" }}><MapPin style={{ width: 10, height: 10 }} /> {result.location}</span>}
          {result.remote && <span>{t("ai.resultsRemote")}</span>}
        </div>
        <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
          <Link to={`/jobs/${result.job_id}`} data-testid={`ai-result-job-view-${result.job_id}`}
            onClick={() => track("ai_job_recommendation_clicked", { job_id: result.job_id, match_score: result.match_score })}
            style={{ flex: 1, textAlign: "center", fontSize: "12px", fontWeight: 600, padding: "6px 10px", borderRadius: "8px", border: "1px solid #028CF8", color: "#028CF8", textDecoration: "none" }}>
            {t("ai.viewJobBtn")}
          </Link>
          <button onClick={toggleSave} disabled={saving} data-testid={`ai-result-job-save-${result.job_id}`}
            style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 600, padding: "6px 10px", borderRadius: "8px", border: "none", background: saved ? "#F55FA5" : "#028CF8", color: "white", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saved ? <BookmarkCheck style={{ width: 12, height: 12 }} /> : <Bookmark style={{ width: 12, height: 12 }} />}
            {saved ? t("ai.unsaveBtn") : t("ai.saveBtn")}
          </button>
        </div>
      </div>
    );
  }

  if (result.type === "candidate") {
    return (
      <div data-testid={`ai-result-candidate-${result.application_id}`} style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "12px", padding: "10px 12px", marginTop: "6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", lineHeight: 1.3 }}>{result.name}</p>
            <p style={{ fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif" }}>{result.title || result.job_title}</p>
          </div>
          <ScoreBadge score={result.match_score} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px", fontSize: "11px", color: "#94A3B8" }}>
          {result.location && <span style={{ display: "flex", alignItems: "center", gap: "3px" }}><MapPin style={{ width: 10, height: 10 }} /> {result.location}</span>}
          {!!result.years_experience && <span>{result.years_experience}y exp.</span>}
        </div>
        <Link to={`/company/dashboard?tab=applicants&open_application=${result.application_id}`} data-testid={`ai-result-candidate-view-${result.application_id}`}
          style={{ display: "block", textAlign: "center", marginTop: "8px", fontSize: "12px", fontWeight: 600, padding: "6px 10px", borderRadius: "8px", border: "1px solid #028CF8", color: "#028CF8", textDecoration: "none" }}>
          {t("ai.viewProfileBtn")}
        </Link>
      </div>
    );
  }

  return null;
}
